import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { mobile } from "../../design-system/tokens/breakpoints";

// eyebrow and description are optional so the 40 existing call sites keep
// rendering exactly as before.
const ScreenHeader = ({ header, eyebrow, breadcrumb, description, children, labelDataCy }) => {
    return (
        <HeaderRow>
            <Titles>
                {/* A breadcrumb is sentence case; the eyebrow slot uppercases and
                    letter-spaces whatever it is given, so it cannot carry one. */}
                {breadcrumb}
                {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
                {header && <Title data-cy={labelDataCy}>{header}</Title>}
                {description && <Description>{description}</Description>}
            </Titles>
            <Children>{children}</Children>
        </HeaderRow>
    );
};

export default ScreenHeader;

ScreenHeader.propTypes = {
    header: PropTypes.node,
    eyebrow: PropTypes.node,
    breadcrumb: PropTypes.node,
    description: PropTypes.node,
    children: PropTypes.node,
    labelDataCy: PropTypes.string,
};

const HeaderRow = styled.div`
    display: grid;
    align-items: center;
    justify-content: space-between;
    grid-template-rows: 1fr;
    column-gap: var(--gap-xxxl);
    grid-template-columns: max-content 1fr;

    ${mobile} {
        grid-template-columns: 1fr;
        row-gap: var(--gap-l);
        align-items: start;
    }
`;

const Titles = styled.div`
    min-width: 0;

`;

const Eyebrow = styled.div`
    font-size: var(--pw-text-2xs);
    font-weight: var(--pw-weight-bold);
    letter-spacing: var(--pw-tracking-eyebrow);
    text-transform: uppercase;
    color: var(--pw-brand-cyan);
`;

// 22px at regular weight, as every screen title in the prototype is — a bold
// title at this size competes with the card headings beneath it.
const Title = styled.h3`
    /* The prototype sets the heading 9px below whatever precedes it — a trail or an eyebrow —
       and flush when it is first. Declared here rather than as a parent child-rule, which
       loses the specificity tie against this block's own margin. */
    margin: 9px 0 0;

    &:first-child {
        margin-top: 0;
    }
    font-size: var(--pw-text-page);
    font-weight: var(--pw-weight-regular);
    color: var(--text-heading);
    line-height: var(--pw-leading-tight);
`;

const Description = styled.p`
    margin: var(--gap-m) 0 0;
    max-width: 620px;
    font-family: var(--pw-font-mono);
    font-size: var(--pw-text-base);
    font-weight: var(--pw-weight-light);
    color: var(--text-muted);
    line-height: var(--pw-leading-normal);
    text-wrap: pretty;
`;

const Children = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: var(--gap-l);
    align-content: start;
    align-items: flex-start;

    ${mobile} {
        justify-content: flex-start;
    }
`;
